import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button } from 'palette/Button';
import { useConfig } from '@common/Hooks/useConfig';
import { useTranslation } from '@common/Hooks/useTranslation';
import { Product } from '@common/Listing/types';
import { MyListsCard } from '@common/MyLists/containers/MyListsCard';
import { openFavorites } from '@common/MyLists/store/actions/MyListsActions';
import { ItemsParameters, ItemInteractionOrigin } from '@common/Product/AddToFavorites/types';
import { CARD_TYPES } from '@common/ProductCard/helpers';
import { trackProductImpressions } from '@common/Tracking';
import { getStoreEnv } from '@helpers/env';
import { getCardTranslations } from '../helpers/cardTranslations';
import '../styles/Products.scss';

interface FavoritesProductsProps {
    products: Product[];
    pageSize: number;
    className: string;
    isRightSticky: boolean;
    itemsParameters: ItemsParameters;
    interactionOrigin: ItemInteractionOrigin;
}

function FavoritesProducts({
    products,
    className,
    pageSize,
    isRightSticky,
    itemsParameters,
    interactionOrigin,
}: FavoritesProductsProps) {
    const dispatch = useDispatch();
    const config = useConfig();
    const translate = useTranslation();

    const productsContainerRef = useRef<HTMLUListElement | null>(null);
    const [containerWidth, setContainerWidth] = useState<number | undefined>();

    const myPageSize = pageSize || products.length;

    const openFavoritesClick = () => {
        dispatch(openFavorites());
    };

    useEffect(() => {
        if (['booztmobile', 'boozt'].indexOf(getStoreEnv(config)) !== -1 && productsContainerRef) {
            trackProductImpressions(productsContainerRef.current);
            setContainerWidth(productsContainerRef.current?.offsetWidth);
        }
    }, [config]);

    return (
        <ul
            ref={productsContainerRef}
            className={classNames(
                className,
                'product-tabs__list',
                'product-tabs__list--favourites',
                'product-tabs__list--product-card-redesign',
                {
                    'is-empty': products.length <= 0,
                    'product-tabs__list--empty': products.length === 0,
                },
            )}
            data-current-page={1}
            data-products-group={itemsParameters.item_list_name}
            data-products-per-page={myPageSize}
            data-total-products={products.length}
        >
            {products.length > 0 ? (
                products
                    .slice(0, 1 * myPageSize)
                    .map((product, index) => {
                        const bmpTrackingData = {
                            totalProducts: products.length,
                            productsPerPage: myPageSize,
                            currentPage: 1,
                            itemIndex: index,
                            group: itemsParameters.item_list_name,
                            containerOffsetWidth: containerWidth,
                        };
                        const cardType = isRightSticky
                            ? CARD_TYPES.RFListingRightSticky
                            : CARD_TYPES.RFListingLeftSticky;
                        return (
                            <MyListsCard
                                key={`${product.variants[0].id}_${cardType}`}
                                cardStrings={getCardTranslations(translate)}
                                containerClassName={classNames(
                                    'product-mini-list__item',
                                    'product-mini-list__item--product-card-redesign',
                                )}
                                product={product}
                                trackingValues={{
                                    list: itemsParameters.item_list_name,
                                    bmp: bmpTrackingData,
                                    itemsParameters,
                                    interactionOrigin,
                                }}
                                type={cardType}
                            />
                        );
                    })
            ) : (
                <li className="message-empty">{translate('customer_my_lists_favourites_empty')}</li>
            )}
            {products.length > myPageSize
                    && (
                        <li
                            className="view-all"
                        >
                            <Button
                                isExpanded
                                onClick={openFavoritesClick}
                            >
                                {translate('helper.view_all.label')}
                            </Button>
                        </li>
                    )}
        </ul>
    );
}

export default FavoritesProducts;
